<template>
  <div class="emp-picker">
    <div v-if="modelValue" :class="['emp-picker-value', { locked: disabled }]">
      <employeeAvatar :employee="modelValue" :size="34" />
      <span class="main">
        <span class="name">{{ nameOf(modelValue) }}</span>
        <span v-if="contactOf(modelValue)" class="sub">{{ contactOf(modelValue) }}</span>
      </span>
      <button v-if="!disabled" type="button" class="btn btn-custom-secondary btn-rec btn-sm" @click="clear">Change</button>
    </div>

    <div v-else :ref="(el) => (anchorEl = el)" :class="['emp-picker-search', { invalid }]">
      <i class="pi pi-search"></i>
      <input ref="input" v-model="term" type="text" class="form-control filter-input" :placeholder="placeholder" autocomplete="off"
        role="combobox" :aria-expanded="open ? 'true' : 'false'" :aria-invalid="invalid ? 'true' : 'false'"
        @focus="openList" @input="onInput" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.enter.prevent="pickActive" @keydown.esc.stop="open = false" />
      <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
    </div>

    <dashPopover :open="open && !modelValue" :anchor="anchorEl" :minWidth="width" @close="open = false">
      <div class="emp-picker-list" role="listbox">
        <div v-if="loading && !results.length" class="emp-picker-empty">Searching employees</div>
        <div v-else-if="!results.length" class="emp-picker-empty">{{ term.trim() ? 'No active employee matches' : 'No active employees yet' }}</div>
        <button v-for="(employee, index) in results" :key="employee.id" type="button" role="option" :aria-selected="index === active ? 'true' : 'false'"
          :class="['emp-picker-option', { active: index === active }]" @mouseenter="active = index" @mousedown.prevent @click="pick(employee)">
          <employeeAvatar :employee="employee" :size="28" />
          <span class="main">
            <span class="name">{{ nameOf(employee) }}</span>
            <span v-if="contactOf(employee)" class="sub">{{ contactOf(employee) }}</span>
          </span>
        </button>
      </div>
    </dashPopover>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import Employee from '@/services/employee';
import employeeAvatar from '@/components/dashboard/Employee_Avatar.vue';
import dashPopover from '@/components/dashboard/Dash_Popover.vue';
import { fullName } from '@/utils/employee';

/** Searchable single-employee field. v-model is the selected employee object. */
export default {
  name: 'EmployeePicker',
  components: { employeeAvatar, dashPopover },
  props: {
    modelValue: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Search by name or phone number' },
  },
  emits: ['update:modelValue'],
  data() {
    return { term: '', results: [], loading: false, open: false, active: 0, anchorEl: null, width: 280, requestId: 0 };
  },
  computed: {
    organizationId() {
      const account = this.$store.getters.account.data;
      return account?.organization?.id ?? account?.organizationId;
    },
  },
  created() {
    this.debouncedSearch = debounce(() => this.search(), 250);
  },
  beforeUnmount() {
    this.debouncedSearch.cancel();
  },
  methods: {
    nameOf: fullName,
    contactOf(employee) {
      return employee?.email || employee?.phoneNumber || '';
    },
    openList() {
      if (this.disabled) return;
      this.width = this.anchorEl?.offsetWidth || 280;
      this.open = true;
      if (!this.results.length && !this.loading) this.search();
    },
    onInput() {
      this.open = true;
      this.loading = true;
      this.debouncedSearch();
    },
    async search() {
      const requestId = ++this.requestId;
      this.loading = true;
      const res = await Employee.getEmployees({
        organizationId: this.organizationId,
        searchTerm: this.term.trim() || undefined,
        page: 1,
        limit: 12,
      }).catch(() => null);
      if (requestId !== this.requestId) return;
      const list = res?.data?.status && Array.isArray(res.data.data) ? res.data.data : [];
      this.results = list.filter((employee) => employee.isActive !== false).slice(0, 8);
      this.active = 0;
      this.loading = false;
    },
    move(step) {
      if (!this.open) {
        this.openList();
        return;
      }
      const total = this.results.length;
      if (total) this.active = (this.active + step + total) % total;
    },
    pickActive() {
      const employee = this.results[this.active];
      if (this.open && employee) this.pick(employee);
    },
    pick(employee) {
      this.open = false;
      this.term = '';
      this.$emit('update:modelValue', employee);
    },
    clear() {
      this.$emit('update:modelValue', null);
      this.$nextTick(() => this.$refs.input?.focus());
    },
  },
};
</script>

<style scoped>
.emp-picker-value {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px 7px 10px;
  border: 1px solid #DCDCE0;
  border-radius: 10px;
  background: #ffffff;
  min-width: 0;
}

.emp-picker-value.locked {
  background: #FAFAFA;
}

.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  line-height: 1.25;
  text-align: left;
}

.name {
  font-size: 0.84rem;
  font-weight: 600;
  color: #18181b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sub {
  font-size: 0.72rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.emp-picker-search {
  position: relative;
}

.emp-picker-search .pi-search {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #9ca3af;
  pointer-events: none;
}

.emp-picker-search input {
  padding-left: 34px;
  padding-right: 34px;
}

.emp-picker-search.invalid input {
  border-color: #dc2626 !important;
}

.emp-picker-search .spinner-border {
  position: absolute;
  right: 12px;
  top: 50%;
  margin-top: -0.5rem;
  color: #9ca3af;
}

.emp-picker-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 300px;
  overflow-y: auto;
}

.emp-picker-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  min-width: 0;
}

.emp-picker-option.active {
  background: #f0f9f0;
}

.emp-picker-empty {
  padding: 12px 10px;
  font-size: 0.78rem;
  color: #6b7280;
  text-align: center;
}
</style>
